import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  InventoryDrilldownKind,
  InventoryDrilldownRow,
  InventoryReportQuery,
  InventoryStockHealth,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { formatMoney } from '@/modules/catalog/money';
import { useLocale } from '@/hooks/useLocale';
import { ExportCsvButton } from './ExportCsvButton';
import { useInventoryRecordsQuery } from '../api/reports.api';

const PAGE_SIZE = 20;

export interface InventoryRecordsRequest {
  kind: InventoryDrilldownKind;
  health?: InventoryStockHealth;
  /** What the panel is titled, already translated. */
  title: string;
}

type Column = {
  key: string;
  label: string;
  end?: boolean;
  cell: (row: InventoryDrilldownRow) => ReactNode;
};

/**
 * The records behind one Inventory figure.
 *
 * Paged and filtered by the server under the report's own scope, so these are exactly the lines the
 * figure counted. Quantities are shown per line, never totalled across product types; a line's cost
 * appears only when the server sent it.
 */
export function InventoryRecords({
  query,
  request,
  baseCurrencyCode,
  onSelectVariant,
  onClose,
}: {
  query: InventoryReportQuery;
  request: InventoryRecordsRequest;
  baseCurrencyCode: string;
  onSelectVariant?: (variantId: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [page, setPage] = useState(1);
  const recordsQuery = {
    ...query,
    kind: request.kind,
    health: request.health,
    page,
    pageSize: PAGE_SIZE,
  };
  const records = useInventoryRecordsQuery(recordsQuery);
  const pages = records.data ? Math.max(1, Math.ceil(records.data.total / PAGE_SIZE)) : 1;
  const headingId = `inventory-records-${request.kind}-${request.health ?? 'all'}`;

  const ltr = (value: ReactNode) => <span dir="ltr">{value}</span>;
  const product: Column = {
    key: 'product',
    label: t('reports.inventory.columns.product'),
    cell: (row) => (
      <>
        <span className="text-ink">{row.productName ?? t('reports.sales.unattributed')}</span>
        <span className="text-muted ms-2 text-xs" dir="ltr">
          {row.sku}
        </span>
        {onSelectVariant && !query.variantId ? (
          <button
            type="button"
            onClick={() => onSelectVariant(row.variantId)}
            className="text-brand-700 ms-2 text-xs font-medium hover:underline"
            aria-label={t('reports.inventory.filters.onlyItemFor', { sku: row.sku })}
          >
            {t('reports.inventory.filters.onlyItem')}
          </button>
        ) : null}
      </>
    ),
  };
  const branch: Column = {
    key: 'branch',
    label: t('reports.inventory.columns.branch'),
    cell: (row) => row.branchName,
  };
  const number = (
    key: string,
    label: string,
    pick: (row: InventoryDrilldownRow) => number | null,
  ) => ({
    key,
    label,
    end: true,
    cell: (row: InventoryDrilldownRow) => {
      const value = pick(row);
      return value === null ? '—' : ltr(value);
    },
  });

  const columns: Column[] =
    request.kind === 'STOCK_LINES' || request.kind === 'ZERO_SALES'
      ? [
          product,
          branch,
          number('quantity', t('reports.inventory.columns.onHand'), (row) => row.quantity),
          {
            key: 'state',
            label: t('reports.inventory.columns.state'),
            cell: (row) =>
              [
                row.alert && row.alert !== 'OK' ? t(`reports.inventory.alert.${row.alert}`) : null,
                row.ageing ? t(`reports.inventory.ageing.${row.ageing}`) : null,
              ]
                .filter(Boolean)
                .join(' · ') || '—',
          },
          number('age', t('reports.inventory.columns.ageDays'), (row) => row.ageDays),
          ...(records.data?.rows.some((row) => row.costMinor !== null)
            ? [
                {
                  key: 'value',
                  label: t('reports.inventory.columns.value'),
                  end: true,
                  cell: (row: InventoryDrilldownRow) =>
                    row.valueMinor === null
                      ? t('reports.inventory.noCost')
                      : ltr(formatMoney(row.valueMinor, locale, baseCurrencyCode)),
                },
              ]
            : []),
        ]
      : request.kind === 'MOVEMENTS'
        ? [
            {
              key: 'date',
              label: t('reports.inventory.columns.date'),
              cell: (row) => ltr(row.businessDate),
            },
            {
              key: 'category',
              label: t('reports.inventory.columns.movement'),
              cell: (row) => (row.category ? t(`reports.inventory.movement.${row.category}`) : '—'),
            },
            product,
            branch,
            number('quantity', t('reports.inventory.columns.quantity'), (row) => row.quantity),
          ]
        : request.kind === 'STOCKTAKE_LINES'
          ? [
              {
                key: 'reference',
                label: t('reports.inventory.columns.reference'),
                cell: (row) => ltr(row.reference),
              },
              {
                key: 'date',
                label: t('reports.inventory.columns.date'),
                cell: (row) => ltr(row.businessDate),
              },
              product,
              number('expected', t('reports.inventory.columns.expected'), (row) => row.expected),
              number('counted', t('reports.inventory.columns.counted'), (row) => row.counted),
              number('variance', t('reports.inventory.columns.variance'), (row) => row.variance),
            ]
          : [
              {
                key: 'reference',
                label: t('reports.inventory.columns.reference'),
                cell: (row) => ltr(row.reference),
              },
              {
                key: 'route',
                label: t('reports.inventory.columns.route'),
                cell: (row) => `${row.branchName} → ${row.counterpartBranchName ?? '—'}`,
              },
              product,
              number(
                'dispatched',
                t('reports.inventory.columns.dispatched'),
                (row) => row.dispatched,
              ),
              number('received', t('reports.inventory.columns.received'), (row) => row.received),
              request.kind === 'IN_TRANSIT'
                ? number(
                    'remaining',
                    t('reports.inventory.columns.remaining'),
                    (row) => row.quantity,
                  )
                : number(
                    'variance',
                    t('reports.inventory.columns.variance'),
                    (row) => row.variance,
                  ),
            ];

  return (
    <Card className="p-4">
      <section aria-labelledby={headingId}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h3 id={headingId} className="text-ink font-semibold">
            {request.title}
          </h3>
          <span className="flex flex-wrap items-baseline gap-3">
            <ExportCsvButton
              target={{
                method: 'GET',
                path: '/reports/inventory/records/export',
                query: recordsQuery,
              }}
            />
            <button
              type="button"
              onClick={onClose}
              className="text-brand-700 text-sm font-medium hover:underline"
            >
              {t('reports.sales.records.close')}
            </button>
          </span>
        </div>
        {records.isError ? (
          <div className="mt-3">
            <Alert tone="error">{t('reports.sales.records.loadError')}</Alert>
          </div>
        ) : null}
        {records.isPending ? (
          <div
            className="bg-surface border-line mt-3 h-24 animate-pulse rounded-lg border"
            aria-hidden
          />
        ) : null}
        {records.data && records.data.rows.length === 0 ? (
          <p className="text-muted mt-3 text-sm">{t('reports.sales.records.empty')}</p>
        ) : null}
        {records.data && records.data.rows.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[36rem] text-sm">
              <thead>
                <tr className="text-muted border-line border-b">
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`py-2 font-medium ${column.end ? 'text-end' : 'text-start'}`}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.data.rows.map((row) => (
                  <tr
                    key={`${row.kind}-${row.id}-${row.branchId}`}
                    className="border-line border-b last:border-b-0"
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`py-2 ${column.end ? 'text-end tabular-nums' : ''}`}
                      >
                        {column.cell(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {records.data && pages > 1 ? (
          <div className="mt-3 flex items-center justify-between gap-3 text-sm">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
              className="text-brand-700 font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('reports.sales.records.previous')}
            </button>
            <span className="text-muted">{t('reports.sales.records.page', { page, pages })}</span>
            <button
              type="button"
              disabled={page >= pages}
              onClick={() => setPage((current) => current + 1)}
              className="text-brand-700 font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('reports.sales.records.next')}
            </button>
          </div>
        ) : null}
      </section>
    </Card>
  );
}
